import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMechanicAuth } from '../MechanicAuthContext';
import axios from 'axios';
import {
  Gauge, Trash2, X, ChevronDown, ChevronUp, Truck, Container,
  AlertTriangle, CheckCircle, Clock, ArrowLeft, Save, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import AxleDiagram from '../../components/AxleDiagram';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  good: 'text-green-400',
  warning: 'text-amber-400',
  critical: 'text-red-400',
  none: 'text-slate-400',
};

export default function MechanicMaintenancePage() {
  const { token } = useMechanicAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedVehicleId = searchParams.get('vehicle');

  const [vehicles, setVehicles] = useState([]);
  const [measurementStatus, setMeasurementStatus] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showMeasureForm, setShowMeasureForm] = useState(false);
  const [measureHistory, setMeasureHistory] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [vehRes, msRes] = await Promise.all([
        axios.get(`${API_URL}/api/mechanic/fleet/vehicles`, { headers }),
        axios.get(`${API_URL}/api/mechanic/fleet/measurement-status`, { headers }),
      ]);
      const vList = vehRes.data || [];
      setVehicles(vList);
      setMeasurementStatus(msRes.data?.statuses || {});

      if (preselectedVehicleId) {
        const found = vList.find(v => v.id === preselectedVehicleId);
        if (found) {
          setSelectedVehicle(found);
          setShowMeasureForm(true);
        }
      }
    } catch {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, preselectedVehicleId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSelectVehicle = async (vehicle) => {
    setSelectedVehicle(vehicle);
    setShowMeasureForm(false);
    try {
      const res = await axios.get(`${API_URL}/api/mechanic/fleet/vehicles/${vehicle.id}/measurements`, { headers });
      setMeasureHistory(res.data || []);
    } catch { setMeasureHistory([]); }
  };

  const handleMeasureSaved = () => {
    setShowMeasureForm(false);
    fetchData();
    if (selectedVehicle) handleSelectVehicle(selectedVehicle);
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center pb-20">
      <div className="w-10 h-10 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
    </div>
  );

  // If measuring a specific vehicle
  if (showMeasureForm && selectedVehicle) {
    return (
      <MeasurementForm
        token={token}
        vehicle={selectedVehicle}
        onBack={() => { if (preselectedVehicleId) navigate('/admin/mecanicien/flotte'); else setShowMeasureForm(false); }}
        onSaved={handleMeasureSaved}
      />
    );
  }

  // If vehicle detail view with history
  if (selectedVehicle) {
    const ms = measurementStatus[selectedVehicle.id] || {};
    return (
      <div className="min-h-screen bg-slate-950 pb-24" data-testid="mechanic-maintenance-detail">
        <div className="bg-gradient-to-b from-slate-900 to-slate-950 px-4 pt-6 pb-4">
          <button onClick={() => setSelectedVehicle(null)} className="flex items-center gap-1 text-slate-400 text-sm mb-3">
            <ArrowLeft size={16} /> Retour
          </button>
          <div className="flex items-center gap-3 mb-4">
            {selectedVehicle.vehicle_type === 'trailer' ? <Container size={24} className="text-slate-400" /> : <Truck size={24} className="text-amber-400" />}
            <div>
              <h1 className="text-lg font-bold text-white">
                {selectedVehicle.unit_number ? `#${selectedVehicle.unit_number} ` : ''}{selectedVehicle.plate_number || 'Sans plaque'}
              </h1>
              <p className="text-slate-400 text-sm">{selectedVehicle.brand} {selectedVehicle.model} {selectedVehicle.year}</p>
            </div>
            <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${
              selectedVehicle.fleet_source === 'atelier' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'
            }`}>
              {selectedVehicle.fleet_source === 'atelier' ? 'Client' : 'Admin'}
            </span>
          </div>

          {/* Measurement Status */}
          <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-3 mb-4">
            <p className="text-xs text-slate-400 mb-2">Etat des mesures</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-xs text-slate-500">Freins</p>
                <p className={`text-sm font-bold ${STATUS_COLORS[ms.brake_status || 'none']}`}>
                  {ms.min_brake != null ? `${ms.min_brake}mm` : 'Aucune mesure'}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Pneus</p>
                <p className={`text-sm font-bold ${STATUS_COLORS[ms.tire_status || 'none']}`}>
                  {ms.min_tire != null ? `${ms.min_tire}mm` : 'Aucune mesure'}
                </p>
              </div>
            </div>
            {ms.last_date && <p className="text-[10px] text-slate-500 mt-2">Derniere mesure: {ms.last_date}</p>}
          </div>

          <button
            data-testid="maintenance-goto-workorder-btn"
            onClick={() => navigate('/admin/mecanicien/bons/new')}
            className="w-full bg-amber-500 text-black font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-2"
          >
            <Plus size={16} /> Creer un bon de travail
          </button>
        </div>

        {/* History */}
        <div className="px-4 mt-4">
          <h2 className="text-white font-semibold text-sm mb-3">Historique des mesures</h2>
          {measureHistory.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-6">Aucune mesure enregistree</p>
          ) : (
            <div className="space-y-2">
              {measureHistory.map(rec => (
                <MeasurementCard key={rec.id} record={rec} />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Vehicle list view
  return (
    <div className="min-h-screen bg-slate-950 pb-24" data-testid="mechanic-maintenance-page">
      <div className="bg-gradient-to-b from-slate-900 to-slate-950 px-4 pt-6 pb-4">
        <h1 className="text-xl font-bold text-white mb-2">Mesures pneus et freins</h1>
        <p className="text-slate-400 text-sm mb-4">Selectionnez un vehicule pour voir ou ajouter des mesures</p>
      </div>

      <div className="px-4 space-y-2">
        {vehicles.map(v => {
          const ms = measurementStatus[v.id] || {};
          return (
            <button
              key={v.id}
              onClick={() => handleSelectVehicle(v)}
              className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl p-3 text-left"
              data-testid={`maintenance-vehicle-${v.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {v.vehicle_type === 'trailer' ? <Container size={16} className="text-slate-400" /> : <Truck size={16} className="text-amber-400" />}
                  <div>
                    <p className="text-white text-sm font-medium">
                      {v.unit_number ? `#${v.unit_number} ` : ''}{v.plate_number || ''}
                    </p>
                    <p className="text-slate-500 text-xs">{v.brand} {v.model}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${
                    v.fleet_source === 'atelier' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'
                  }`}>
                    {v.fleet_source === 'atelier' ? 'Client' : 'Admin'}
                  </span>
                  {ms.status && ms.status !== 'none' && (
                    <span className={`text-xs font-medium ${STATUS_COLORS[ms.status]}`}>
                      {ms.status === 'good' ? <CheckCircle size={14} /> : ms.status === 'warning' ? <AlertTriangle size={14} /> : <AlertTriangle size={14} />}
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MeasurementCard({ record }) {
  const [expanded, setExpanded] = useState(false);
  const axles = record.axles || [];

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
      <button onClick={() => setExpanded(!expanded)} className="w-full p-3 text-left">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white text-sm font-medium">{record.measurement_date}</p>
            <p className="text-slate-500 text-xs">
              {axles.length} essieu(x) - {record.odometer > 0 ? `${record.odometer.toLocaleString()} km` : ''}
              {record.created_by_name ? ` - ${record.created_by_name}` : ''}
            </p>
          </div>
          {expanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
        </div>
      </button>
      {expanded && (
        <div className="border-t border-slate-700/50 p-3 space-y-2">
          {axles.map((axle, i) => (
            <div key={i} className="bg-slate-900/50 rounded-lg p-2">
              <p className="text-xs text-amber-400 font-medium mb-1">Essieu {axle.axle_number || i + 1}</p>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                <span className="text-slate-500">Frein G: <span className="text-white">{axle.left_brake ?? '-'}</span></span>
                <span className="text-slate-500">Frein D: <span className="text-white">{axle.right_brake ?? '-'}</span></span>
                <span className="text-slate-500">Pneu G int: <span className="text-white">{axle.left_tire_inner ?? '-'}</span></span>
                <span className="text-slate-500">Pneu D int: <span className="text-white">{axle.right_tire_inner ?? '-'}</span></span>
                {axle.has_dual_tires && (
                  <>
                    <span className="text-slate-500">Pneu G ext: <span className="text-white">{axle.left_tire_outer ?? '-'}</span></span>
                    <span className="text-slate-500">Pneu D ext: <span className="text-white">{axle.right_tire_outer ?? '-'}</span></span>
                  </>
                )}
              </div>
            </div>
          ))}
          {record.notes && <p className="text-xs text-slate-400 mt-1">Notes: {record.notes}</p>}
        </div>
      )}
    </div>
  );
}

function MeasurementForm({ token, vehicle, onBack, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    measurement_date: today,
    odometer: vehicle.current_odometer || 0,
    brake_unit: 'mm',
    tire_unit: 'mm',
    notes: '',
  });
  const mkAxle = (n, dual) => ({ axle_number: n, has_dual_tires: dual, left_brake: null, left_tire_inner: null, left_tire_outer: null, right_brake: null, right_tire_inner: null, right_tire_outer: null });
  const [axles, setAxles] = useState([mkAxle(1, false), mkAxle(2, true)]);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const inputClass = "w-full px-3 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white text-sm placeholder-slate-500 focus:ring-2 focus:ring-amber-500 focus:border-transparent";

  const updateAxle = (idx, field, value) => {
    setAxles(prev => prev.map((a, i) => i === idx ? { ...a, [field]: value } : a));
  };
  const toggleDual = (idx) => {
    setAxles(prev => prev.map((a, i) => i === idx ? { ...a, has_dual_tires: !a.has_dual_tires, left_tire_outer: !a.has_dual_tires ? a.left_tire_outer : null, right_tire_outer: !a.has_dual_tires ? a.right_tire_outer : null } : a));
  };
  const addAxle = () => {
    const nextNum = axles.length > 0 ? Math.max(...axles.map(a => a.axle_number)) + 1 : 1;
    setAxles(prev => [...prev, mkAxle(nextNum, true)]);
  };
  const removeAxle = () => {
    if (axles.length <= 1) return;
    setAxles(prev => prev.slice(0, -1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        odometer: parseInt(form.odometer) || 0,
        axles: axles.map(a => ({
          axle_number: a.axle_number,
          has_dual_tires: a.has_dual_tires,
          left_brake: a.left_brake,
          right_brake: a.right_brake,
          left_tire_inner: a.left_tire_inner,
          left_tire_outer: a.has_dual_tires ? a.left_tire_outer : null,
          right_tire_inner: a.right_tire_inner,
          right_tire_outer: a.has_dual_tires ? a.right_tire_outer : null,
        })),
      };
      await axios.post(`${API_URL}/api/mechanic/fleet/vehicles/${vehicle.id}/measurements`, payload, { headers });
      toast.success('Mesures enregistrees');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 pb-24" data-testid="mechanic-measure-form">
      <div className="bg-gradient-to-b from-slate-900 to-slate-950 px-4 pt-6 pb-4">
        <button onClick={onBack} className="flex items-center gap-1 text-slate-400 text-sm mb-3">
          <ArrowLeft size={16} /> Retour
        </button>
        <h1 className="text-lg font-bold text-white mb-1">Mesures freins/pneus</h1>
        <p className="text-amber-400 text-sm">
          {vehicle.unit_number ? `#${vehicle.unit_number} ` : ''}{vehicle.plate_number} - {vehicle.brand} {vehicle.model}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="px-4 mt-4 space-y-4">
        {/* Date & Odometer */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Date</label>
            <input type="date" value={form.measurement_date} onChange={e => setForm(f => ({ ...f, measurement_date: e.target.value }))} className={inputClass} />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Odometre (km)</label>
            <input type="number" value={form.odometer} onChange={e => setForm(f => ({ ...f, odometer: e.target.value }))} className={inputClass} />
          </div>
        </div>

        {/* Unified Axle Diagram */}
        <AxleDiagram
          axles={axles}
          brakeUnit={form.brake_unit}
          tireUnit={form.tire_unit}
          onUpdateAxle={updateAxle}
          onToggleDual={toggleDual}
          onAddAxle={addAxle}
          onRemoveAxle={removeAxle}
          onBrakeUnitChange={v => setForm(f => ({ ...f, brake_unit: v }))}
          onTireUnitChange={v => setForm(f => ({ ...f, tire_unit: v }))}
          dark={true}
          compact={true}
        />

        {/* Notes */}
        <div>
          <label className="block text-xs text-slate-400 mb-1">Notes</label>
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className={inputClass} rows={2} placeholder="Observations..." />
        </div>

        <button
          type="submit"
          disabled={saving}
          data-testid="measurement-save-btn"
          className="w-full bg-amber-500 text-black font-bold py-3 rounded-xl text-sm flex items-center justify-center gap-2 disabled:opacity-50"
        >
          <Save size={16} /> {saving ? 'Enregistrement...' : 'Enregistrer les mesures'}
        </button>
      </form>
    </div>
  );
}
